// DEPENDENCIES
import React, { useState, useEffect, useContext } from "react";
import dayjs from "dayjs";

// CSS
import "./DesignCategories.css";

// MSAL
import { useMsal } from "@azure/msal-react";

// API IMPORTS
import { getAPICall, postAPICall } from "../../config/apiCalls";

// CONTEXT
import { UserContext } from "../../context/UserContext";

// MUI COMPONENTS
import { Button, CircularProgress, IconButton } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";

// CUSTOM COMPONENTS
import TanStackTable from "../Misc/Table/TanStackTable";
import J2NoResultsFound from "../Misc/J2NoResultsFound/J2NoResultsFound";
import DesignCategoriesEditAdd from "./DesignCategoriesEditAdd/DesignCategoriesEditAdd";

// CONSTANTS
import { defaultSort, notApplicable } from "../Misc/Table/TanStackTableConstants";

// FUNCTIONAL COMPONENT
const DesignCategories = (props) => {
	// MSAL
	const { instance, accounts } = useMsal();

	// CONTEXT
	const employeeID = useContext(UserContext);

	// STATES
	const [designCategories, setDesignCategories] = useState([]);
	const [columns, setColumns] = useState([]);
	const [rows, setRows] = useState([]);

	// Feedback
	const [isLoading, setIsLoading] = useState(false);

	// Dialogs
	const [editAddOpen, setEditAddOpen] = useState(false);
	const [editing, setEditing] = useState(false);
	const [recordToEdit, setRecordToEdit] = useState(null);

	// USE EFFECT
	useEffect(() => {
		fetchData();
		postAPICall(instance, accounts[0], "/api/logs/access/add", {
			page_name: "Design Categories",
			employeeID: employeeID,
			date_time: dayjs().format("MMMM D, YYYY h:mm:ss A"),
			notes: "Accessed the Design Categories page",
		});
	}, []);

	useEffect(() => {
		if (designCategories.length > 0) {
			buildColumns();
			buildRows();
		}
	}, [designCategories]);

	// API FUNCTIONS
	const fetchData = async () => {
		setIsLoading(true);
		try {
			const res = await getAPICall(instance, accounts[0], "/api/encompass/designCategories/all");
			setDesignCategories(res.data);
		} catch (err) {
			console.error("Error fetching design categories:", err);
		}
		setIsLoading(false);
	};

	// HELPER FUNCTIONS
	const buildColumns = () => {
		setColumns([
			{
				accessorKey: "title",
				header: () => <span>Title</span>,
				label: "Title",
				cell: (props) => (
					<span>{props.getValue() ?? notApplicable}</span>
				),
				filterType: "textfield",
				filterFn: defaultSort,
			},
			{
				accessorKey: "edit",
				header: "",
				label: "Edit",
				cell: ({ row }) => (
					<IconButton
						onClick={() => onEditClick(row.original.record)}
						sx={{ justifyContent: "flex-end" }}
						disableRipple={true}
					>
						<EditIcon fontSize="small" />
					</IconButton>
				),
				enableSorting: false,
				enableColumnFilter: false,
			},
		]);
	};

	const buildRows = () => {
		setRows(
			designCategories.map((c) => ({
				id: c.id,
				title: c.title,
				edit: "",
				record: c,
			}))
		);
	};

	// Dialogs
	const onAddClick = () => {
		setEditing(false);
		setRecordToEdit(null);
		setEditAddOpen(true);
	};

	const onEditClick = (rec) => {
		setEditing(true);
		setRecordToEdit(rec);
		setEditAddOpen(true);
	};

	const onDialogClose = (refresh) => {
		setEditAddOpen(false);
		setRecordToEdit(null);
		setEditing(false);
		if (refresh) fetchData();
	};

	// RENDER
	return (
		<div className="DesignCategories">
			<div className="designCategoriesHeader">
				<div className="designCategoriesHeaderTitle">
					Design Categories
					<div className="designCategoriesHeaderButtons">
						<Button
							variant="contained"
							color="primary"
							onClick={onAddClick}
							disabled={isLoading}
						>
							New Record
						</Button>
					</div>
				</div>
			</div>
			{isLoading && (
				<div className="loadingDiv">
					<CircularProgress color="primary" />
				</div>
			)}
			{!isLoading && (
				<div className="tableContainerDiv">
					{designCategories.length === 0 ? (
						<J2NoResultsFound />
					) : (
						<TanStackTable
							columnData={columns}
							rowData={rows}
							defaultSort={[{ id: "name", desc: false }]}
							showSearch={true}
						/>
					)}
				</div>
			)}
			<DesignCategoriesEditAdd
				open={editAddOpen}
				editing={editing}
				recordToEdit={recordToEdit}
				onClose={onDialogClose}
			/>
		</div>
	);
};

export default DesignCategories;
